import type { AdmissionFormValues } from '@/features/institute/admissions/schemas/admission.schema';
import {
  ADMISSION_TYPE_LABEL,
  SELECTABLE_ADMISSION_TYPES,
} from '@/features/institute/admissions/constants';
import { EMPTY_ADMISSION_FORM } from '@/features/institute/admissions/utils';
import { formatAadhaar } from '@/lib/forms';
import { GENDER_LABEL, type StudentGender } from '@/constants/institute';
import { STUDENT_STATUS_LABEL } from '@/constants/studentStatus';
import type { Student, StudentListParams, StudentStatus } from '@/types/student';

/**
 * Turning a stored record back into the form that wrote it, and back again.
 *
 * The edit screen deliberately reuses the admission form's sections rather than
 * growing a second set of fields: two forms describing one child is how a
 * institute ends up able to enter a middle name at admission and not afterwards.
 */

/** Every field the edit screen sends. The fees are not among them. */
export const EDITABLE_FIELDS = [
  'firstName',
  'middleName',
  'lastName',
  'email',
  'gender',
  'dateOfBirth',
  'bloodGroup',
  'religion',
  'category',
  'nationality',
  'aadhaarNumber',
  'classUuid',
  'sectionUuid',
  'admissionDate',
  'admissionType',
  'previousSchool',
  'previousClass',
  'transferCertificateNumber',
  'fatherName',
  'fatherOccupation',
  'fatherMobile',
  'fatherEmail',
  'motherName',
  'motherOccupation',
  'motherMobile',
  'motherEmail',
  'guardianName',
  'guardianRelationship',
  'guardianMobile',
  'primaryContact',
  'addressLine1',
  'addressLine2',
  'city',
  'district',
  'state',
  'pincode',
  'usesTransport',
  'routeName',
  'busNumber',
  'pickupPoint',
] as const satisfies readonly (keyof AdmissionFormValues)[];

/** `null` from the API is an empty input, not the string "null". */
const text = (value: string | null | undefined): string => value ?? '';

/**
 * Fills the admission form with a stored record.
 *
 * The fee fields are filled from what the child was charged so the shared
 * resolver still validates, and the edit screen simply does not render them —
 * a fee correction is a fee transaction, not an edit to a child's details.
 */
export function toAdmissionForm(student: Student): AdmissionFormValues {
  return {
    ...EMPTY_ADMISSION_FORM,

    firstName: student.firstName,
    middleName: text(student.middleName),
    lastName: student.lastName,
    email: text(student.email),
    gender: student.gender,
    dateOfBirth: student.dateOfBirth,
    bloodGroup: student.bloodGroup,
    religion: text(student.religion),
    category: student.category,
    nationality: student.nationality,
    aadhaarNumber: formatAadhaar(text(student.aadhaarNumber)),

    classUuid: text(student.classUuid),
    sectionUuid: text(student.sectionUuid),
    admissionDate: student.admissionDate,
    // A retired quota type (RTE, management, staff ward) was never a transfer,
    // so the hidden field holds Regular. It is not sent unless the office
    // chooses to replace the stored type — see `retiredAdmissionTypeLabel`.
    admissionType: student.admissionType === 'TRANSFER' ? 'TRANSFER' : 'REGULAR',
    previousSchool: text(student.previousSchool),
    previousClass: text(student.previousClass),
    transferCertificateNumber: text(student.transferCertificateNumber),

    fatherName: text(student.fatherName),
    fatherOccupation: text(student.fatherOccupation),
    fatherMobile: text(student.fatherMobile),
    fatherEmail: text(student.fatherEmail),
    motherName: text(student.motherName),
    motherOccupation: text(student.motherOccupation),
    motherMobile: text(student.motherMobile),
    motherEmail: text(student.motherEmail),
    guardianName: text(student.guardianName),
    guardianRelationship: text(student.guardianRelationship),
    guardianMobile: text(student.guardianMobile),
    primaryContact: student.primaryContact,

    addressLine1: student.addressLine1,
    addressLine2: text(student.addressLine2),
    city: student.city,
    district: text(student.district),
    state: student.state,
    pincode: text(student.pincode),

    usesTransport: student.usesTransport,
    routeName: text(student.routeName),
    busNumber: text(student.busNumber),
    pickupPoint: text(student.pickupPoint),

    // Kept so the shared resolver has something valid to check. Never sent.
    tuitionFee: wholeRupees(student.fees?.tuitionFee),
    tuitionDiscount: wholeRupees(student.fees?.tuitionDiscount),
    tuitionPaid: wholeRupees(student.fees?.tuitionPaid),
    booksFee: wholeRupees(student.fees?.booksFee),
    booksDiscount: wholeRupees(student.fees?.booksDiscount),
    booksPaid: wholeRupees(student.fees?.booksPaid),
    otherFee: wholeRupees(student.fees?.otherFee),
    otherDiscount: wholeRupees(student.fees?.otherDiscount),
    otherPaid: wholeRupees(student.fees?.otherPaid),
    concessionReason: text(student.fees?.concessionReason),
    feeDueDate: '',
  };
}

/** `"36000.00"` → `"36000"`. The form only ever holds whole rupees. */
function wholeRupees(amount: string | undefined): string {
  if (!amount) return '0';
  return String(Math.round(Number(amount)));
}

/**
 * The label of a stored admission type the form can no longer offer (a retired
 * quota type), or null when the form can show the record's type as it is.
 *
 * The edit screen shows such a type read-only and leaves it out of the save,
 * so correcting a child's address does not quietly rewrite them as Regular.
 */
export function retiredAdmissionTypeLabel(student: Student): string | null {
  const selectable: readonly string[] = SELECTABLE_ADMISSION_TYPES;
  return selectable.includes(student.admissionType) ? null : ADMISSION_TYPE_LABEL[student.admissionType];
}

/**
 * The payload `PATCH /institute/students/:uuid` takes: the record fields the form
 * parsed, plus the status, and nothing else. Built from an explicit list rather
 * than by deleting keys, so a field added to the admission form later is not
 * silently sent here too.
 */
export function toUpdatePayload(
  parsed: Record<string, unknown>,
  status: StudentStatus,
  { keepAdmissionType = false }: { keepAdmissionType?: boolean } = {},
): Record<string, unknown> {
  const payload: Record<string, unknown> = { status };

  for (const field of EDITABLE_FIELDS) {
    // Omitted, the server leaves the stored type alone: how a retired quota
    // type survives a correction to something else on the record.
    if (field === 'admissionType' && keepAdmissionType) continue;
    // `undefined` is how the shared schema reports "left blank"; omitting the
    // key is what tells the server the same thing.
    if (parsed[field] !== undefined) payload[field] = parsed[field];
  }

  return payload;
}

// ---------------------------------------------------------------------------
// the register's address
// ---------------------------------------------------------------------------

/**
 * The register's filters, kept in its address.
 *
 * Opening a child and pressing Back used to land on the whole school again,
 * the class and section the office had just picked gone. With the filters in
 * the address, Back, the browser's own Back and a reload all return to the
 * same list, and the edit screen only has to remember one string.
 */

/** The query keys the register writes. Anything else in an address is ignored. */
const REGISTER_KEYS = [
  'page',
  'search',
  'classUuid',
  'sectionUuid',
  'status',
  'gender',
  'newThisYear',
  'documentsPending',
  'houseUuid',
  'sort',
  'attendanceMonth',
  'academicYearUuid',
] as const;

const SORTS = ['name', 'admissionNumber', 'rollNumber', 'newest'] as const;

function oneOf<T extends string>(value: string | null, allowed: readonly T[]): T | undefined {
  return allowed.find((option) => option === value);
}

/**
 * Filters read from an address, over the defaults. A value that is not one the
 * screen could have written — a hand-edited status, a page of "abc" — falls back
 * to the default rather than reaching the server as a bad request.
 */
export function registerFiltersFrom(
  params: URLSearchParams,
  defaults: StudentListParams,
): StudentListParams {
  const page = Number(params.get('page'));
  const month = params.get('attendanceMonth');
  const flag = (key: 'newThisYear' | 'documentsPending') =>
    params.get(key) === 'true' ? ('true' as const) : '';

  return {
    ...defaults,
    page: Number.isInteger(page) && page > 0 ? page : defaults.page,
    search: params.get('search') ?? defaults.search,
    classUuid: params.get('classUuid') ?? defaults.classUuid,
    sectionUuid: params.get('sectionUuid') ?? defaults.sectionUuid,
    status:
      oneOf(params.get('status'), Object.keys(STUDENT_STATUS_LABEL) as StudentStatus[]) ??
      defaults.status,
    gender:
      oneOf(params.get('gender'), Object.keys(GENDER_LABEL) as StudentGender[]) ?? defaults.gender,
    newThisYear: flag('newThisYear') || defaults.newThisYear,
    documentsPending: flag('documentsPending') || defaults.documentsPending,
    houseUuid: params.get('houseUuid') ?? defaults.houseUuid,
    sort: oneOf(params.get('sort'), SORTS) ?? defaults.sort,
    attendanceMonth: month && /^\d{4}-\d{2}$/.test(month) ? month : defaults.attendanceMonth,
    academicYearUuid: params.get('academicYearUuid') ?? defaults.academicYearUuid,
  };
}

/** The address for these filters, leaving out whatever is still the default. */
export function registerSearchFor(
  filters: StudentListParams,
  defaults: StudentListParams,
): URLSearchParams {
  const params = new URLSearchParams();
  for (const key of REGISTER_KEYS) {
    const value = filters[key];
    if (value !== undefined && value !== '' && value !== defaults[key]) {
      params.set(key, String(value));
    }
  }
  return params;
}

/**
 * Carried in navigation state from the register to a child's record, so the
 * record's Back returns to the list exactly as it was left.
 */
export interface RegisterReturnState {
  registerSearch: string;
}

/** The register's address to go back to, from whatever state a record was opened with. */
export function registerPathFrom(state: unknown): string {
  const search =
    typeof state === 'object' && state !== null && 'registerSearch' in state
      ? (state as RegisterReturnState).registerSearch
      : '';
  return typeof search === 'string' && search.startsWith('?')
    ? `/admin/students${search}`
    : '/admin/students';
}
